import { Download, FileText, LoaderCircle, Share2, X, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { canShareAttachment, fetchAttachmentFile, isAttachmentImage } from "../lib/attachmentDownload";
import { formatBytes } from "../lib/format";
import type { Attachment } from "../lib/types";
import type { BrowserWorkspace } from "../lib/workspace";

export function AttachmentViewer({ attachment, workspace, onClose }: {
  attachment: Attachment;
  workspace: BrowserWorkspace;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [loaded, setLoaded] = useState<{ file: File; url: string } | null>(null);
  const [error, setError] = useState("");
  const [imageFailed, setImageFailed] = useState(false);
  const [fullSize, setFullSize] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");

  useEffect(() => {
    const node = dialog.current;
    node?.showModal();
    return () => { node?.close(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    const timeout = window.setTimeout(() => {
      controller.abort();
      setError("The download took too long. Check your connection and try again.");
    }, 60_000);
    setLoaded(null);
    setError("");
    setShareError("");
    setImageFailed(false);
    setFullSize(false);
    void fetchAttachmentFile(attachment, workspace, controller.signal).then((file) => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(file);
      setLoaded({ file, url: objectUrl });
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "This file could not be opened.");
    }).finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.name, workspace, attempt]);

  async function share() {
    if (!loaded || sharing) return;
    setSharing(true);
    setShareError("");
    try {
      // Load first, then share directly from a click (iOS transient activation).
      // Only share the file bytes, never a protected URL or session credential.
      await navigator.share({ files: [loaded.file] });
    } catch (reason) {
      if (!(reason instanceof Error && reason.name === "AbortError")) {
        setShareError("Sharing is unavailable. Use Download to save this file instead.");
      }
    } finally { setSharing(false); }
  }

  const shareable = loaded && canShareAttachment(loaded.file);
  return (
    <dialog ref={dialog} className="attachment-viewer" aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="attachment-viewer__panel">
        <header className="attachment-viewer__header">
          <div><h2 id={titleId}>{attachment.name}</h2><small>{formatBytes(loaded?.file.size ?? attachment.size)}</small></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close preview" autoFocus><X aria-hidden="true" size={20} /></button>
        </header>
        <div className={`attachment-viewer__content${fullSize ? " attachment-viewer__content--full-size" : ""}`} aria-busy={!loaded && !error}>
          {error ? <div className="attachment-viewer__state"><p role="alert">{error}</p>
            <button type="button" className="button button--secondary" onClick={() => setAttempt((value) => value + 1)}>Try again</button></div>
            : !loaded ? <div className="attachment-viewer__state" role="status"><LoaderCircle className="spin" aria-hidden="true" /><p>Loading file…</p></div>
            : isAttachmentImage(loaded.file) && !imageFailed
              ? <img className="attachment-viewer__image" src={loaded.url} alt={attachment.name} onError={() => setImageFailed(true)} />
              : <div className="attachment-viewer__state"><FileText aria-hidden="true" size={36} />
                <p>{imageFailed ? "This image format cannot be previewed on this device." : "This file is ready to save or open in another app."}</p></div>}
        </div>
        {loaded && <footer className="attachment-viewer__footer">
          {attachment.scanBypassed && attachment.scanStatus !== "clean" && <p className="attachment-viewer__warning">Scanner disabled: open this file at your own risk.</p>}
          {shareable && <p>Use Save or share to save to Files or open with an app on your device.</p>}
          {shareError && <p role="alert">{shareError}</p>}
          <div className="attachment-viewer__actions">
            {isAttachmentImage(loaded.file) && !imageFailed && <button type="button" className="button button--secondary"
              onClick={() => setFullSize((value) => !value)} aria-pressed={fullSize}>
              {fullSize ? <ZoomOut aria-hidden="true" size={16} /> : <ZoomIn aria-hidden="true" size={16} />}
              {fullSize ? "Fit image" : "Actual size"}</button>}
            {shareable && <button type="button" className="button button--primary" onClick={() => void share()} disabled={sharing}>
              <Share2 aria-hidden="true" size={16} />{sharing ? "Sharing…" : "Save or share"}</button>}
            <a className={`button ${shareable ? "button--secondary" : "button--primary"}`} href={loaded.url} download={loaded.file.name}>
              <Download aria-hidden="true" size={16} />Download</a>
          </div>
        </footer>}
      </div>
    </dialog>
  );
}
